/**
 * ContactCardMessage
 *
 * Dedicated visual rendering for messages tagged with
 * `message_type === 'contact_card'` (or whose plain-text body is parsed
 * into a contact-card payload by `parseContactCardText`).
 *
 * Props:
 * - `card`: structured payload (see backend
 *   `services/contact_card_persistence.py`). Required fields are
 *   tolerated as missing — every row is conditionally rendered.
 * - `direction`: `'inbound'` (contact shared their vCard with us) or
 *   `'outbound'` (we sent the referent's contact to the user).
 * - `senderLabel` / `senderIcon`: already-localised header strings, so
 *   this component does not have to re-derive sender attribution.
 * - `timestamp`: ISO datetime to render under the body.
 *
 * URL safety: only `https?://` and `wa.me/<digits>` schemes are
 * rendered as anchors. Everything else (e.g. `javascript:`) falls back
 * to plain text.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import './ContactCardMessage.css';

const SAFE_HTTP_RX = /^https?:\/\//i;
const SAFE_WAME_RX = /^https:\/\/wa\.me\/\d+/i;

function isSafeUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return SAFE_HTTP_RX.test(url) || SAFE_WAME_RX.test(url);
}

function deriveInitials(card) {
  const name = card?.name || {};
  if (name.first || name.last) {
    const a = (name.first || '').trim();
    const b = (name.last || '').trim();
    const initials = `${a.charAt(0)}${b.charAt(0)}`.toUpperCase();
    if (initials) return initials;
  }
  if (name.formatted) {
    const parts = name.formatted.trim().split(/\s+/);
    const initials = parts.slice(0, 2).map((p) => p.charAt(0)).join('').toUpperCase();
    if (initials) return initials;
  }
  return null;
}

function formatTimestamp(timestamp) {
  if (!timestamp) return '';
  try {
    return new Date(timestamp).toLocaleString();
  } catch (e) {
    return '';
  }
}

function ContactCardMessage({ card, direction, senderLabel, senderIcon, timestamp }) {
  const { t } = useTranslation();
  const normalisedCard = card || {};
  const name = normalisedCard.name || {};
  const displayName = name.formatted
    || [name.first, name.last].filter(Boolean).join(' ').trim()
    || t('contactCardMessage.header');
  const initials = deriveInitials(normalisedCard);

  const isInbound = direction === 'inbound';
  const sourceBadge = isInbound
    ? t('contactCardMessage.sharedByContact')
    : t('contactCardMessage.sentToContact');

  const phone = normalisedCard.phone;
  const email = normalisedCard.email;
  const referralLink = normalisedCard.referral_link;
  const waMeLink = normalisedCard.wa_me_link;

  const hasAnyDetail = !!(phone || email || referralLink || waMeLink);

  const renderUrl = (url, label) => {
    if (!isSafeUrl(url)) {
      return <span className="contact-card-row-value">{url}</span>;
    }
    return (
      <a
        className="contact-card-row-value contact-card-link"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
      >
        {url}
      </a>
    );
  };

  return (
    <div
      className={`contact-card-message ${isInbound ? 'inbound' : 'outbound'}`}
      role="group"
      aria-label={t('contactCardMessage.cardOf', { name: displayName })}
    >
      <div className="contact-card-header">
        <div className="contact-card-avatar" aria-hidden="true">
          {initials || '📇'}
        </div>
        <div className="contact-card-heading">
          {senderLabel && (
            <div className="contact-card-sender">
              {senderIcon ? <span className="contact-card-sender-icon">{senderIcon}</span> : null}
              <span>{senderLabel}</span>
            </div>
          )}
          <div className="contact-card-name" title={displayName}>{displayName}</div>
          <div className="contact-card-badge">{sourceBadge}</div>
        </div>
      </div>

      <div className="contact-card-body">
        {phone ? (
          <div className="contact-card-row">
            <span className="contact-card-row-icon" aria-hidden="true">📞</span>
            <span className="contact-card-row-label">{t('contactCardMessage.phone')}</span>
            <a
              className="contact-card-row-value contact-card-link"
              href={`tel:${phone}`}
              aria-label={`${t('contactCardMessage.phone')} ${phone}`}
            >
              {phone}
            </a>
            {isSafeUrl(waMeLink) ? (
              <a
                className="contact-card-cta"
                href={waMeLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('contactCardMessage.openInWhatsApp')}
              >
                💬 {t('contactCardMessage.openInWhatsApp')}
              </a>
            ) : null}
          </div>
        ) : null}

        {email ? (
          <div className="contact-card-row">
            <span className="contact-card-row-icon" aria-hidden="true">✉️</span>
            <span className="contact-card-row-label">{t('contactCardMessage.email')}</span>
            <a
              className="contact-card-row-value contact-card-link"
              href={`mailto:${email}`}
              aria-label={`${t('contactCardMessage.email')} ${email}`}
            >
              {email}
            </a>
          </div>
        ) : null}

        {referralLink ? (
          <div className="contact-card-row">
            <span className="contact-card-row-icon" aria-hidden="true">🔗</span>
            <span className="contact-card-row-label">{t('contactCardMessage.link')}</span>
            {renderUrl(referralLink, t('contactCardMessage.link'))}
          </div>
        ) : null}

        {!hasAnyDetail ? (
          <div className="contact-card-empty">
            {t('contactCardMessage.noDetails')}
          </div>
        ) : null}
      </div>

      {timestamp ? (
        <div className="contact-card-footer">
          <span className="contact-card-timestamp">{formatTimestamp(timestamp)}</span>
        </div>
      ) : null}
    </div>
  );
}

export default ContactCardMessage;
